import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addBlogAPI,
  viewBlog,
  editBlogAPI,
  deleteBlogAPI,
  viewAllBlog,
  viewTodayBlog,
} from "../Apis/Blog";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../Apis/firebase"; // Import the initialized storage

const initialState = {
  data: [],
  totalRecords: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 10,
  loading: false,
  error: null,
};

// Allowed file types
const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
const validVideoTypes = ["video/mp4", "video/webm", "video/avi"];
// Async thunk to upload media files to Firebase and then add to the gallery

export const addBlog = createAsyncThunk(
  "blog/register",
  async (
    { category, mediaFiles, heading, writtenBy, htmlContent },
    { rejectWithValue }
  ) => {
    try {
      console.log("Media files:", mediaFiles);

      // Validate media files
      if (!mediaFiles || mediaFiles.length === 0) {
        throw new Error("Please upload at least one valid media file.");
      }

      let mediaURLs = [];

      // Upload all files in parallel
      const uploadPromises = mediaFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const fileType = file.type;
          let fileBlog = "";

          // Validate file type
          if (validImageTypes.includes(fileType)) {
            fileBlog = "images";
          } else if (validVideoTypes.includes(fileType)) {
            fileBlog = "videos";
          } else {
            reject(
              new Error(
                "Invalid file type. Only images and videos are allowed."
              )
            );
            return;
          }

          // Generate a unique file name
          const fileName = `${file.name}-${Math.random()
            .toString(36)
            .substr(2, 9)}.${file.name.split(".").pop()}`;
          const storageRef = ref(storage, `/Blogs/${fileBlog}/${fileName}`);

          // Upload the file
          const uploadTask = uploadBytesResumable(storageRef, file);

          uploadTask.on(
            "state_changed",
            null,
            (error) => reject(error),
            async () => {
              try {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                resolve({ url: downloadURL, type: fileBlog });
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      });

      // Wait for all uploads to complete
      mediaURLs = await Promise.all(uploadPromises);

      // Call the API to store the blog with the media URLs
      const response = await addBlogAPI(
        category,
        mediaURLs,
        heading,
        writtenBy,
        htmlContent
      );

      return response;
    } catch (error) {
      console.error("Error in addBlog thunk:", error);
      return rejectWithValue(error.message || "Failed to add blog");
    }
  }
);

export const editBlog = createAsyncThunk(
  "blog/update",
  async (
    {
      id,
      category,
      heading,
      writtenBy,
      htmlContent,
      imageFiles,
      newImageFiles,
    },
    { rejectWithValue }
  ) => {
    try {
      let mediaURLs = [];

      // If there are new files to upload, process them
      if (newImageFiles && newImageFiles.length > 0) {
        // const storage = getStorage();

        // Upload all files in parallel
        const uploadPromises = newImageFiles.map((file) => {
          return new Promise((resolve, reject) => {
            const fileType = file.type;
            let fileBlog = "";

            // Validate file type
            if (validImageTypes.includes(fileType)) {
              fileBlog = "images";
            } else if (validVideoTypes.includes(fileType)) {
              fileBlog = "videos";
            } else {
              reject("Invalid file type. Only images and videos are allowed.");
              return;
            }

            // Generate a unique file name
            const fileName = `${file.name}-${Math.random()
              .toString(36)
              .substr(2, 9)}.${file.name.split(".").pop()}`;
            const storageRef = ref(storage, `/Blogs/${fileBlog}/${fileName}`);

            // Upload the file
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
              "state_changed",
              null,
              (error) => reject(error),
              async () => {
                try {
                  const downloadURL = await getDownloadURL(
                    uploadTask.snapshot.ref
                  );
                  resolve({ url: downloadURL, type: fileBlog });
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
        });

        // Wait for all uploads to complete
        mediaURLs = await Promise.all(uploadPromises);
      }

      // Call the API to update the blog with the new media URLs
      const response = await editBlogAPI(
        id,
        category,
        heading,
        writtenBy,
        htmlContent,
        newImageFiles && newImageFiles.length > 0 ? mediaURLs : imageFiles
      );

      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update blog");
    }
  }
);

export const deleteBlog = createAsyncThunk(
  "blog/delete",
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteBlogAPI(id);

      dispatch(fetchBlog({ page: 1, pageSize: 10 }));

      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Blog Delete failed");
    }
  }
);

export const fetchBlog = createAsyncThunk(
  "blog/view",
  async ({ page, pageSize }, { rejectWithValue }) => {
    try {
      const response = await viewBlog(page, pageSize);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);

export const fetchCategoryBlog = createAsyncThunk(
  "blog/viewBlogByCategory",
  async ({ category }, { rejectWithValue }) => {
    try {
      const response = await viewAllBlog(category);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);

export const fetchLatestBlog = createAsyncThunk(
  "blog/viewLatestBlog",
  async (_, { rejectWithValue }) => {
    try {
      const response = await viewTodayBlog();
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);

const gallerySlice = createSlice({
  name: "blog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle media upload and gallery addition
      .addCase(addBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.blogData?.unshift(action.payload);
        state.totalRecords += 1;

        if (state.totalRecords % state.pageSize === 0) {
          state.totalPages += 1;
        }
      })
      .addCase(addBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })
      //edit
      //   .addCase(editBlog.pending, (state) => {
      //     state.loading = true;
      //     state.error = null;
      //   })
      //   .addCase(editBlog.fulfilled, (state, action) => {
      //     state.loading = false;
      //     state.fetchAgain = true;
      //     state.error = null;
      //     // state.blogData.unshift(action.payload);
      //   })
      //   .addCase(editBlog.rejected, (state, action) => {
      //     state.loading = false;
      //     state.error = action.payload || "Something went wrong";
      //   })

      //delete
      .addCase(deleteBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      // Handle fetching gallery data
      .addCase(fetchBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlog.fulfilled, (state, action) => {
        state.loading = false;

        state.blogData = action.payload?.data;
        state.totalRecords = action.payload?.totalRecords;
        state.totalPages = action.payload?.totalPages;
        state.currentPage = action.payload?.currentPage;
        state.pageSize = action.payload?.pageSize;
      })
      .addCase(fetchBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle fetching gallery data
      .addCase(fetchCategoryBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryBlog.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
        
        state.blogAllData = action.payload;
      })
      .addCase(fetchCategoryBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetching gallery data
      .addCase(fetchLatestBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLatestBlog.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
        
        state.blogLatestData = action.payload;
      })
      .addCase(fetchLatestBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default gallerySlice.reducer;
